import React, { useState, useRef } from 'react'; // Import useRef
import { Bar } from 'react-chartjs-2';
import { colorFromString } from '../utils/colors';

// Helper function to format date string
const formatDate = (dateString) => {
  const date = new Date(dateString);
  // Use UTC to avoid timezone issues
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const MainChart = ({ data, title = 'Resultados por Fecha', onSegmentClick, onVisibleLabelsChange }) => {
  const [dateOffset, setDateOffset] = useState(0);
  const chartRef = useRef(null); // Create a ref for the chart instance

  const allDates = [...new Set(data.map(item => item.fecha))]
    .map(dateString => new Date(dateString))
    .sort((a, b) => a - b);

  const lastDate = allDates.length > 0 ? allDates[allDates.length - 1] : new Date();

  const endDate = new Date(lastDate);
  endDate.setDate(endDate.getDate() - dateOffset);

  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - 29);

  const visibleDates = [];
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    visibleDates.push(formatDate(d));
  }

  const chartData = {
    labels: visibleDates,
    datasets: [...new Set(data.map(item => item.indicador))].map((indicator) => {
      return {
        label: indicator,
        data: visibleDates.map(date => {
          const itemsForDateAndIndicator = data.filter(d => formatDate(d.fecha) === date && d.indicador === indicator);
          return itemsForDateAndIndicator.reduce((sum, item) => sum + parseInt(item.valor, 10), 0);
        }),
        backgroundColor: colorFromString(indicator),
      };
    }),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        ...(onVisibleLabelsChange && {
          onClick: (e, legendItem, legend) => {
            // Replica el toggle default de Chart.js y además avisa qué series quedaron visibles
            const index = legendItem.datasetIndex;
            const ci = legend.chart;
            if (ci.isDatasetVisible(index)) {
              ci.hide(index);
              legendItem.hidden = true;
            } else {
              ci.show(index);
              legendItem.hidden = false;
            }
            const visibleLabels = ci.data.datasets
              .filter((_, i) => ci.isDatasetVisible(i))
              .map(ds => ds.label);
            onVisibleLabelsChange(visibleLabels);
          },
        }),
      },
      title: {
        display: true,
        text: title, // Use the title prop here
      },
      datalabels: {
        display: (context) => {
          return context.datasetIndex === context.chart.data.datasets.length - 1;
        },
        formatter: (value, context) => {
          const datasets = context.chart.data.datasets;
          let total = 0;
          for (let i = 0; i < datasets.length; i++) {
            total += parseFloat(datasets[i].data[context.dataIndex]);
          }
          return total.toString();
        },
        rotation: -90,
        color: '#000',
        anchor: 'end',
        align: 'end',
        offset: -5,
      }
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
  };

  const handlePrev = () => {
    setDateOffset(dateOffset + 30);
  };

  const handleNext = () => {
    setDateOffset(Math.max(0, dateOffset - 30));
  };

  const isPrevDisabled = allDates.length === 0 || startDate <= allDates[0];
  const isNextDisabled = dateOffset === 0;

  const handleChartClick = (event) => { // Removed 'elements' and 'chart' from args
    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    // Use getElementsAtEventForMode for more robust element detection
    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false);
    
    console.log('Chart clicked! elements:', elements);

    if (elements && elements.length > 0) {
      const { datasetIndex, index } = elements[0];
      const clickedDate = chart.data.labels[index];
      const clickedIndicator = chart.data.datasets[datasetIndex].label;

      if (onSegmentClick) {
        onSegmentClick(clickedDate, clickedIndicator);
      }
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{title}</h5> {/* Use the title prop here as well for the card header */}
        <div>
          <button className="btn btn-outline-secondary btn-sm me-2" onClick={handlePrev} disabled={isPrevDisabled}>Anteriores</button>
          <button className="btn btn-outline-secondary btn-sm" onClick={handleNext} disabled={isNextDisabled}>Siguientes</button>
        </div>
      </div>
      <div className="card-body">
        <div style={{ height: '400px' }}>
          <Bar ref={chartRef} options={chartOptions} data={chartData} onClick={handleChartClick} /> {/* Attach ref */}
        </div>
      </div>
    </div>
  );
};

export default MainChart;
